import { navigate, RouteComponentProps } from '@reach/router';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import css from './DataRequestForm.module.scss';
import { Input } from '../Input/Input';
import { escapeHtml, logger } from '../../utils';
import { Button } from '../Button/Button';
import { api } from '../../state/effects';
import { DataRequest, EmailStatus, EmailType } from '../../../interfaces/customer-api';
import { useOvermindState } from '../../state';
import { Spinner } from '../Spinner/Spinner';
import { CompleteRequestDialog } from '../CompleteRequestDialog/CompleteRequestDialog';
import { WidgetBreadcrumb } from '../../../interfaces/internal';
import { SubPage } from '../SubPage/SubPage';

const emailBodyHtmlBuilder = (
    customerNumber: string,
    companyName: string,
    name: string,
    position: string,
    products: string,
    requestDate: string,
    requiredData?: string,
): string => {
    const htmlTemplate = `
       <p>A data request was created by customer: ${escapeHtml(customerNumber || '')}</p>
       <p>Company name: ${escapeHtml(companyName || '')}</p>
       <p>Name: ${escapeHtml(name || '')}</p>
       <p>Position: ${escapeHtml(position || '')}</p>
       <p>Products: ${escapeHtml(products || '')}</p>
       <p>Required data: ${escapeHtml(requiredData || '')}</p>
       <p>Date of request: ${escapeHtml(requestDate || '')}</p>
       </p>
    `;
    // Remove tabs from beginning of each row, so DB won't show unnecessary tabs
    return htmlTemplate.replace(/^\s*\n|\n\s*$/g, '').replace(/^\s*/gm, '');
};

const handleCloseClick = async () => {
    const splittedPath = window.location.pathname.split('/');

    if (splittedPath.length >= 3) {
        await navigate(`/${splittedPath[1]}`);
    } else {
        await navigate('/');
    }
};
interface FormData {
    companyName: string;
    name: string;
    position: string;
    products: string;
    requestDate: string;
    requiredData?: string;
}

export const DataRequestForm: React.FC<RouteComponentProps> = () => {
    const { t } = useTranslation('DataRequestForm');
    const { user, selectedCustomerNumber, selectedCustomer } = useOvermindState();
    // Use a single state for all form values
    const [formData, setFormData] = useState<FormData>({
        companyName: user?.isAdmin ? selectedCustomer?.name || '' : user?.sapCustomerName || '',
        name: user?.name || '',
        position: '',
        products: '',
        requestDate: '',
        requiredData: '',
    });
    const [isDataNoticeAccepted, setIsDataNoticeAccepted] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    // date of data concerned by the request
    const [pastData, setPastData] = useState(false);
    const [presentData, setPresentData] = useState(false);

    const email = 'mykalmar.support@kalmarglobal.com';

    const splittedPath = window.location.pathname.split('/');
    const breadcrumb: WidgetBreadcrumb = {
        id: 'data-request',
        title: t('Data access request'),
        subPage:
            splittedPath.length >= 3
                ? {
                      icon: '',
                      title: t('Fill request form'),
                  }
                : undefined,
    };
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        // Reset the error dialog value
        setShowErrorDialog(false);
        setIsButtonDisabled(true);
        setShowSpinner(true);

        if (selectedCustomerNumber && user && user.id && user.email) {
            const requestDate = [pastData ? 'Past data' : '', presentData ? 'Present data' : '']
                .filter((v) => v.length > 0)
                .join(', ');

            try {
                const request = await api.insertEmail(selectedCustomerNumber, {
                    userId: user.id,
                    customerNumber: selectedCustomerNumber,
                    userName: user.name || '',
                    customerName: (user?.isAdmin ? selectedCustomer?.name : user?.sapCustomerName) || '',
                    senderEmail: user.email,
                    // sentToEmail: string[],
                    emailSubject: `EU data act request form ${selectedCustomerNumber}`,
                    emailBodyJson: {
                        companyName: formData.companyName,
                        name: formData.name,
                        position: formData.position,
                        products: formData.products,
                        requestDate: requestDate,
                        requiredData: formData.requiredData || '',
                    } as DataRequest,
                    emailBodyHtml: emailBodyHtmlBuilder(
                        selectedCustomerNumber,
                        formData.companyName,
                        formData.name,
                        formData.position,
                        formData.products,
                        requestDate,
                        formData.requiredData || '',
                    ),
                    type: EmailType.RequestData,
                    status: EmailStatus.Created,
                    createdOn: Date.now().toString(),
                });
                if (request !== null) {
                    setIsCompleteDialogOpen(true);
                } else {
                    setShowErrorDialog(true);
                    setIsCompleteDialogOpen(true);
                }
            } catch (error) {
                setShowErrorDialog(true);
                setIsCompleteDialogOpen(true);
                logger.error('Error sending data request:', error);
            }
        } else {
            logger.info('No customer selected');
        }
        setShowSpinner(false);
        setIsButtonDisabled(false);
    };

    // Check if all required fields are filled
    useEffect(() => {
        const isFormValid = () => {
            const requiredFields = [formData.companyName, formData.name, formData.position, formData.products];

            if (!requiredFields.every((field) => field.trim() !== '')) {
                return false;
            }
            if (!(pastData || presentData)) {
                return false;
            }
            if (!isDataNoticeAccepted) {
                return false;
            }
            return true;
        };
        setIsButtonDisabled(!isFormValid());
    }, [formData, isDataNoticeAccepted, pastData, presentData]);

    return (
        <SubPage breadcrumb={breadcrumb} onCloseClick={handleCloseClick} idSlug={'data-request'}>
            <form id="data-request-form" onSubmit={handleSubmit}>
                <div className={css.formContainer}>
                    <div className={css.formHead}>{t('Details')}</div>
                    <label id="company-name-label">
                        <span className={css.star}>*</span>
                        {t('Company name')}
                    </label>
                    <Input
                        id="company-name-input"
                        inputProps={{
                            value: formData.companyName,
                            onChange: ({ target }) => setFormData({ ...formData, companyName: target.value }),
                        }}
                    />
                    <label id="name-label">
                        <span className={css.star}>*</span>
                        {t('Name')}
                    </label>
                    <Input
                        id="name-input"
                        inputProps={{
                            value: formData.name,
                            onChange: ({ target }) => setFormData({ ...formData, name: target.value }),
                        }}
                    />
                    <label id="position-label">
                        <span className={css.star}>*</span>
                        {t('Position/relationship with the Company')}
                    </label>
                    <Input
                        inputProps={{
                            value: formData.position,
                            onChange: ({ target }) => setFormData({ ...formData, position: target.value }),
                        }}
                    />
                    <label id="products-label">
                        <span className={css.star}>*</span>
                        {t('Products concerned by the request (e.g. serial number)')}
                    </label>
                    <Input
                        id="products-input"
                        multiline={true}
                        textareaProps={{
                            value: formData.products,
                            onChange: ({ target }) => setFormData({ ...formData, products: target.value }),
                        }}
                    />
                    <label id="date-of-data-label">
                        <span className={css.star}>*</span>
                        {t('Date of data concerned by the request')}
                    </label>
                    <div className={css.dataDate}>
                        <div className={css.dateChoice}>
                            <input
                                id="past-data-input"
                                type="checkbox"
                                checked={pastData}
                                onChange={(e) => setPastData(e.target.checked)}
                            />
                            <label id="past-data-input">{t('Past data')}</label>
                        </div>
                        <div className={css.dateChoice}>
                            <input
                                id="present-data-input"
                                type="checkbox"
                                checked={presentData}
                                onChange={(e) => setPresentData(e.target.checked)}
                            />
                            <label id="present-data-input">{t('Present data')}</label>
                        </div>
                    </div>
                    <label id="required-data-label">{t('Required data')}</label>
                    <Input
                        id="required-data-input"
                        multiline={true}
                        textareaProps={{
                            value: formData.requiredData,
                            onChange: ({ target }) => setFormData({ ...formData, requiredData: target.value }),
                        }}
                    />
                    <label id="accept-label" className={css.acceptLabel}>
                        <span className={css.star}>*</span>
                        {t('Acceptance of Data Notice')}
                    </label>
                    <div className={css.accept}>
                        <input
                            id="accept-checkbox"
                            type="checkbox"
                            checked={isDataNoticeAccepted}
                            onChange={(e) => setIsDataNoticeAccepted(e.target.checked)}
                        />
                        <label id="accept-checkbox">
                            {t('I have read and agree to the')}{' '}
                            <a
                                href="https://www.kalmarglobal.com/legal-notice/eu-data-act/"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ textDecoration: 'underline' }}
                            >
                                {t('Data Act Notice')}
                            </a>
                        </label>
                    </div>
                    <div className={css.alternativeTextContainer}>
                        {t('Alternatively, please contact us at')}
                        <a
                            id="send-email-button"
                            href={`mailto:${email}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'underline' }}
                        >
                            {email}
                        </a>
                    </div>
                    <div className={css.buttonContainer}>
                        <Button
                            type="button"
                            variant="primary"
                            className={css.button}
                            onClick={() => navigate(-1)} /* Navigate back */
                        >
                            {t('Cancel')}
                        </Button>
                        <Button type="submit" variant="primary" className={css.button} isDisabled={isButtonDisabled}>
                            {showSpinner ? (
                                <div className={css.spinner}>
                                    <Spinner size={2} />
                                </div>
                            ) : (
                                t('Send')
                            )}
                        </Button>
                    </div>
                </div>
            </form>
            {isCompleteDialogOpen && (
                <CompleteRequestDialog
                    setIsCompleteDialogOpen={setIsCompleteDialogOpen}
                    isErrorDialog={showErrorDialog}
                    headerText={showErrorDialog ? t('Request submission failed') : t('Data request sent')}
                    successText={t('Thank you for your data request')}
                    errorText={t('Something went wrong')}
                    closeButtonText={t('Close')}
                    onClose={() => {
                        if (!showErrorDialog) navigate(-1);
                    }}
                />
            )}
        </SubPage>
    );
};
